import React from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayCircleOutline as PlayIcon,
  PictureAsPdf as PdfIcon,
  Link as LinkIcon,
  Folder as FolderIcon,
  Construction as ConstructionIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const TrainingResources = () => {
  const { t } = useTranslation();

  const chapters = [
    {
      number: 6,
      title: t('training.ch6Title'),
      icon: <FolderIcon />,
      color: '#1A2B24',
      items: [
        { text: t('training.ch6Item1'), icon: <LinkIcon />, type: 'content', url: 'https://www.canva.com/design/DAGpOhIOkoM/JHx-smTUM6oeZ1cdoAh3qQ/edit?utm_content=DAGpOhIOkoM&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton' },
        { text: t('training.ch6Item2'), icon: <PlayIcon />, type: 'video', url: 'https://www.loom.com/share/907d5966c7da40f69f9ac4e92843c81a' },
        { text: t('training.ch6Item3'), icon: <PdfIcon />, type: 'pdf', url: 'https://acrobat.adobe.com/id/urn:aaid:sc:VA6C2:e3bdebaa-bbd0-41f5-9b20-71f3c29122e8' },
        { text: t('training.ch6Item4'), icon: <PdfIcon />, type: 'pdf', url: 'https://acrobat.adobe.com/id/urn:aaid:sc:VA6C2:fb26c899-7e78-4b33-a24c-5cf981bb1fbc' },
        { text: t('training.ch6Item5'), icon: <PdfIcon />, type: 'pdf', url: 'https://acrobat.adobe.com/id/urn:aaid:sc:VA6C2:08a75130-421c-498e-9811-2e77c0752702' },
        { text: t('training.ch6Item6'), icon: <LinkIcon />, type: 'content', url: 'https://t.me/+84VChMdIvHdiZjgx' },
        { text: t('training.ch6Item7'), icon: <LinkIcon />, type: 'content', url: 'https://try.kixie.com/equityinsurance' },
        { text: t('training.ch6Item8'), icon: <LinkIcon />, type: 'content', url: 'https://nipr.com' },
      ],
    },
  ];

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        {t('training.title', 'Training & Resources')}
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
        {t('training.subtitle', 'Everything you need to succeed — scripts, training, cheat sheets, and more.')}
      </Typography>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'rgba(44, 156, 108, 0.08)',
          border: '1px solid',
          borderColor: 'rgba(44, 156, 108, 0.2)',
          borderRadius: 2,
        }}
      >
        <ConstructionIcon sx={{ color: '#c9a84c', fontSize: 32 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Under Construction
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Training chapters are being updated. Check back soon for new content.
          </Typography>
        </Box>
      </Paper>

      {chapters.map((chapter) => (
        <Accordion
          key={chapter.number}
          defaultExpanded
          sx={{
            mb: 2,
            borderRadius: '8px !important',
            '&::before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`Ch. ${chapter.number}`}
                size="small"
                sx={{
                  bgcolor: chapter.color,
                  color: 'white',
                  fontWeight: 700,
                  minWidth: 56,
                }}
              />
              <Box sx={{ color: chapter.color, display: 'flex', alignItems: 'center' }}>
                {chapter.icon}
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
                {chapter.title}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 2 }}>
            <List disablePadding>
              {chapter.items.map((item, index) => (
                <ListItem
                  key={index}
                  component={item.url ? 'a' : item.internalLink ? 'a' : 'div'}
                  href={item.url || item.internalLink || undefined}
                  target={item.url ? '_blank' : undefined}
                  rel={item.url ? 'noopener noreferrer' : undefined}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    cursor: item.url || item.internalLink ? 'pointer' : 'default',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': item.url || item.internalLink ? {
                      bgcolor: 'action.hover',
                    } : {},
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: chapter.color }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      color: item.url || item.internalLink ? 'primary.main' : 'text.primary',
                      fontWeight: item.url || item.internalLink ? 500 : 400,
                    }}
                  />
                  {item.type === 'pdf' && (
                    <Chip label="PDF" size="small" variant="outlined" color="error" sx={{ ml: 1 }} />
                  )}
                  {item.type === 'video' && (
                    <Chip label="Video" size="small" variant="outlined" color="primary" sx={{ ml: 1 }} />
                  )}
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Container>
  );
};

export default TrainingResources;
